<template>
  <v-container fluid class="pa-6">
    <!-- Page Header -->
    <v-row justify="center" class="mb-6">
      <v-col cols="12" md="10" lg="8">
        <div class="d-flex align-center justify-space-between">
          <div>
            <h1 class="text-h5 font-weight-bold mb-1">Order Detail</h1>
            <p class="text-body-2 text-medium-emphasis mb-0">Imported order information</p>
          </div>
          <v-btn variant="outlined" prepend-icon="mdi-arrow-left" @click="goBack">
            Back to Orders
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row justify="center" v-if="loading">
      <v-col cols="12" md="10" lg="8" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>

    <v-row justify="center" v-if="order && !loading">
      <v-col cols="12" md="10" lg="8">
        <!-- Customer Match Status -->
        <v-alert
          :type="order.isMatched ? 'success' : 'warning'"
          variant="tonal"
          class="mb-4"
        >
          {{ order.isMatched ? 'This order is matched to an existing customer' : 'No matching customer found in the system' }}
        </v-alert>

        <!-- Contact Information -->
        <v-card class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-account</v-icon>
            Contact Information
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start">
              <tbody>
                <tr>
                  <th style="width: 200px;">Contact Name</th>
                  <td>{{ order.contactName || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>Company Name</th>
                  <td>{{ order.companyName || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td>{{ order.email || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>Phone</th>
                  <td>{{ order.phone || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>Fax</th>
                  <td>{{ order.fax || 'N/A' }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Address -->
        <v-card class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-map-marker</v-icon>
            Address
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start">
              <tbody>
                <tr>
                  <th style="width: 200px;">Street</th>
                  <td>{{ order.street || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>Street 2</th>
                  <td>{{ order.street2 || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>City</th>
                  <td>{{ order.city || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>State</th>
                  <td>{{ order.state || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>Zip Code</th>
                  <td>{{ order.zipCode || 'N/A' }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Order Details -->
        <v-card class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-receipt-text</v-icon>
            Order Details
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start">
              <tbody>
                <tr>
                  <th style="width: 200px;">Date Created</th>
                  <td>{{ formatDate(order.dateCreated) }}</td>
                </tr>
                <tr>
                  <th>Event Date</th>
                  <td>{{ order.eventDate || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>Net Sale</th>
                  <td>
                    <span v-if="order.netSale != null" class="font-weight-bold">${{ formatCurrency(order.netSale) }}</span>
                    <span v-else>N/A</span>
                  </td>
                </tr>
                <tr>
                  <th>Gross Sales</th>
                  <td>
                    <span v-if="order.grossSales != null" class="font-weight-bold">${{ formatCurrency(order.grossSales) }}</span>
                    <span v-else>N/A</span>
                  </td>
                </tr>
                <tr>
                  <th>Account Rep</th>
                  <td>{{ order.accountRep || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>Restaurant</th>
                  <td>{{ order.restaurant || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>Order Created By</th>
                  <td>{{ order.orderCreatedBy || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>Industry</th>
                  <td>{{ order.industry || 'N/A' }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Matched Customer Info -->
        <v-card v-if="order.isMatched && order.customer" class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="success" class="mr-2" size="20">mdi-account-check</v-icon>
            Matched Customer
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start">
              <tbody>
                <tr>
                  <th style="width: 200px;">Name</th>
                  <td>{{ order.customer.name }} {{ order.customer.lastName }}</td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td>{{ order.customer.email }}</td>
                </tr>
                <tr>
                  <th>Phone</th>
                  <td>{{ order.customer.phone || 'N/A' }}</td>
                </tr>
                <tr>
                  <th>City</th>
                  <td>{{ order.customer.city || 'N/A' }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { importedOrderService } from '../services';
import { useNotificationStore } from '../stores';

defineOptions({ name: 'ImportedOrderDetailView' });

const route = useRoute();
const router = useRouter();
const notificationStore = useNotificationStore();

const order = ref(null);
const loading = ref(false);

async function loadOrder() {
  loading.value = true;
  try {
    order.value = await importedOrderService.get(route.params.id);
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to load order');
  } finally {
    loading.value = false;
  }
}

function goBack() {
  router.push({ name: 'ImportedOrders' });
}

function formatDate(date) {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCurrency(amount) {
  if (amount == null) return '0.00';
  return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

onMounted(() => {
  loadOrder();
});
</script>
